import { UserProfile, AuditLog } from '@/shared/types';
import { firebaseAuthService } from '@/shared/services/auth/firebaseAuthService';
import { supabaseService } from '@/shared/services/supabaseService';
import { adminServiceAPI } from '@/features/admin/services/adminService-api';
import { authService } from '@/shared/services/realData/auth';

/**
 * Admin operations
 * Handles all admin-related operations
 */
export const adminService = {
  /**
   * Update system configuration
   */
  async adminUpdateSystemConfig(config: any): Promise<void> {
    console.log('Updating system config:', config);
  },

  /**
   * Reset system (dangerous operation)
   */
  async adminResetSystem(): Promise<void> {
    console.warn('System reset requested');
  },

  /**
   * Get admin statistics
   */
  async adminGetStats(): Promise<{
    totalSystemRevenue: number;
    totalClientValue: number;
    activeUsers: number;
    totalTransactions: number;
  }> {
    try {
      // Use the admin service API which now uses direct Supabase
      const adminStats = await adminServiceAPI.getAdminStats();

      return {
        totalSystemRevenue: adminStats.totalRevenue || 0,
        totalClientValue: adminStats.totalRevenue || 0, // For now, using same value
        activeUsers: adminStats.totalUsers || 0,
        totalTransactions: adminStats.totalInvoices || 0
      };
    } catch (error) {
      console.error('Error fetching admin stats:', error);
      // Return default values on error
      return {
        totalSystemRevenue: 0,
        totalClientValue: 0,
        activeUsers: 0,
        totalTransactions: 0
      };
    }
  },

  /**
   * Get all users (admin only)
   */
  async adminGetAllUsers(): Promise<UserProfile[]> {
    try {
      // Check if user is authenticated
      const currentUser = firebaseAuthService.getCurrentUser();
      if (!currentUser) {
        throw new Error('Authentication required');
      }

      // Use the admin service API which now uses direct Supabase
      // This will check admin role within the database query itself
      const adminUsers = await adminServiceAPI.getAllUsers();

      // Transform the data to match UserProfile format
      return adminUsers.map((user: any) => {
        const subscription: any = {
          planId: (user.subscription_plan || 'free') as any,
          status: 'active' as const,
          billingPeriod: 'monthly' as const
        };
        // Only add nextBillingDate if it exists in the data
        if (user.next_billing_date) {
          subscription.nextBillingDate = user.next_billing_date;
        }

        return {
          id: user.id,
          userId: user.id, // Alias for id for compatibility with service layer
          name: user.display_name || user.email,
          email: user.email,
          phone: user.phone || '',
          address: user.address || '',
          currency: (user.currency || 'USD') as any,
          subscription,
          role: (user.role === 'admin' || user.role === 'freelancer' || user.role === 'client' ? user.role : 'freelancer') as 'admin' | 'freelancer' | 'client',
          joinedAt: user.created_at || new Date().toISOString(),
          notificationPreferences: user.notification_preferences || {
            email: {
              newClient: true,
              newQuote: true,
              newInvoice: true,
              paymentReceived: true,
              quoteAccepted: true,
              quoteRejected: true
            },
            whatsapp: {
              newClient: false,
              newQuote: false,
              newInvoice: false,
              paymentReceived: true,
              quoteAccepted: false,
              quoteRejected: false
            }
          },
          brandColor: user.brand_color || '#3B82F6',
          template: (user.template || 'modern') as any,
          country: user.country || 'US',
          savedServices: user.saved_services || [],
          paymentMethods: user.payment_methods || [],
          businessName: user.business_name || '',
          businessAddress: user.business_address || '',
          businessEmail: user.business_email || '',
          businessPhone: user.business_phone || '',
          logoUrl: user.logo_url || '',
          hasCompletedOnboarding: user.has_completed_onboarding || false
        };
      });
    } catch (error) {
      console.error('Error fetching admin users:', error);
      throw error;
    }
  },

  /**
   * Delete a user (admin only)
   */
  async adminDeleteUser(userId: string): Promise<void> {
    try {
      // Use the admin service API which now uses direct Supabase
      await adminServiceAPI.deleteUser(userId);
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  },

  /**
   * Reset user password (admin only)
   */
  async adminResetUserPassword(userId: string): Promise<string> {
    console.log('Resetting password for user:', userId);
    return 'Password reset email sent';
  },

  /**
   * Update user plan (admin only)
   */
  async adminUpdateUserPlan(userId: string, newPlanId: string, billingPeriod: string = 'monthly'): Promise<void> {
    try {
      // Use the admin service API which now uses direct Supabase
      await adminServiceAPI.updateUserPlan(userId, newPlanId, billingPeriod);
    } catch (error) {
      console.error('Error updating user plan:', error);
      throw error;
    }
  },
  /**
   * Update user role (admin only)
   */
  async adminUpdateUserRole(userId: string, newRole: string): Promise<void> {
    try {
      // Update user role via Supabase
      const { error } = await supabaseService.client
        .from('profiles')
        .update({ role: newRole })
        .eq('id', userId);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  },

  /**
   * Suspend a user (admin only)
   */
  async adminSuspendUser(userId: string): Promise<void> {
    console.log('Suspending user:', userId);
  },

  /**
   * Reactivate a user (admin only)
   */
  async adminReactivateUser(userId: string): Promise<void> {
    console.log('Reactivating user:', userId);
  },

  /**
   * Get user activity (admin only)
   */
  async adminGetUserActivity(userId: string): Promise<any[]> {
    console.log('Getting user activity for:', userId);
    return [];
  },

  /**
   * Export user data (admin only)
   */
  async adminExportUserData(userId: string): Promise<any> {
    console.log('Exporting user data for:', userId);
    return {};
  },

  /**
   * Get all transactions (admin only)
   */
  async adminGetTransactions(): Promise<any[]> {
    try {
      // Fetch real PayPal billing agreements from the database
      const { data: billingAgreements, error: agreementsError } = await supabaseService.client
        .from('paypal_billing_agreements')
        .select(`
          *,
          profiles:user_id (
            email,
            full_name,
            subscription_plan_id
          )
        `)
        .in('status', ['active'])
        .order('created_at', { ascending: false });

      if (agreementsError) {
        console.error('Error fetching billing agreements:', agreementsError);
        return [];
      }

      // Transform billing agreements to transaction format
      const transactions = billingAgreements.map((agreement: any) => {
        const userName = agreement.profiles?.full_name || 'Unknown User';
        const userEmail = agreement.profiles?.email || 'No email';
        const planId = agreement.profiles?.subscription_plan_id || 'free';

        // Calculate amount based on plan
        const getPlanAmount = (plan: string) => {
          switch (plan) {
            case 'basic': return 1;
            case 'pro': return 10;
            case 'enterprise': return 29;
            default: return 0;
          }
        };

        return {
          id: agreement.id,
          paypal_agreement_id: agreement.paypal_agreement_id,
          date: agreement.activated_at || agreement.created_at,
          amount: getPlanAmount(planId),
          currency: agreement.currency || 'USD',
          status: agreement.activated_at ? 'succeeded' : 'pending',
          type: 'subscription',
          description: agreement.description || `${planId} Plan - ${agreement.billing_period} Subscription`,
          userId: agreement.user_id,
          userName: userName,
          userEmail: userEmail,
          planId: planId,
          billingPeriod: agreement.billing_period,
          nextBillingDate: agreement.next_billing_date,
          payerId: agreement.payer_id
        };
      });

      return transactions;
    } catch (error) {
      console.error('Error fetching transactions:', error);
      return [];
    }
  },

  /**
   * Get public entity by token
   */
  async getPublicEntity(token: string, type: 'quote' | 'job' | 'invoice'): Promise<any> {
    // This would typically fetch a public entity by its token
    // For now, return null
    console.log('Getting public entity:', { token, type });
    return null;
  },

  /**
   * Get audit logs (admin only)
   */
  async adminGetAuditLogs(filters?: {
    actionType?: string;
    startDate?: string;
    endDate?: string;
    userId?: string;
  }): Promise<AuditLog[]> {
    try {
      // For now, return mock data. In production, this would fetch from a database table
      // TODO: Implement actual audit log table in Supabase
      const mockLogs: AuditLog[] = [
        {
          id: '1',
          action: 'User Deleted',
          actionType: 'user_management',
          details: 'Admin deleted user john.doe@example.com',
          userId: 'user-123',
          adminUserId: 'admin-1',
          adminUserName: 'Admin User',
          adminUserEmail: 'admin@tendaflow.com',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          severity: 'warning',
        },
        {
          id: '2',
          action: 'Plan Changed',
          actionType: 'user_management',
          details: 'Changed plan from free to pro for user jane@example.com',
          userId: 'user-456',
          adminUserId: 'admin-1',
          adminUserName: 'Admin User',
          adminUserEmail: 'admin@tendaflow.com',
          timestamp: new Date(Date.now() - 7200000).toISOString(),
          severity: 'info',
        },
        {
          id: '3',
          action: 'System Config Updated',
          actionType: 'system_config',
          details: 'Updated maintenance mode setting',
          adminUserId: 'admin-1',
          adminUserName: 'Admin User',
          adminUserEmail: 'admin@tendaflow.com',
          timestamp: new Date(Date.now() - 86400000).toISOString(),
          severity: 'info',
        },
      ];

      // Apply filters
      let filteredLogs = mockLogs;

      if (filters?.actionType) {
        filteredLogs = filteredLogs.filter(log => log.actionType === filters.actionType);
      }

      if (filters?.startDate) {
        filteredLogs = filteredLogs.filter(log => new Date(log.timestamp) >= new Date(filters.startDate!));
      }

      if (filters?.endDate) {
        filteredLogs = filteredLogs.filter(log => new Date(log.timestamp) <= new Date(filters.endDate!));
      }

      if (filters?.userId) {
        filteredLogs = filteredLogs.filter(log => log.userId === filters.userId);
      }

      return filteredLogs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      return [];
    }
  },

  /**
   * Create an audit log entry
   */
  async adminCreateAuditLog(entry: Omit<AuditLog, 'id' | 'timestamp'>): Promise<void> {
    try {
      const currentUser = firebaseAuthService.getCurrentUser();
      if (!currentUser) {
        throw new Error('Authentication required');
      }

      // For now, just log to console. In production, this would insert into audit_logs table
      const logEntry: AuditLog = {
        ...entry,
        id: `log-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
        adminUserId: (currentUser as any).uid || currentUser.id || 'unknown',
      };

      console.log('Audit Log Created:', logEntry);

      // TODO: Insert into Supabase audit_logs table
      // await supabaseService.client.from('audit_logs').insert([logEntry]);
    } catch (error) {
      console.error('Error creating audit log:', error);
      throw error;
    }
  },

  /**
   * Export audit logs as CSV
   */
  async adminExportAuditLogs(filters?: {
    actionType?: string;
    startDate?: string;
    endDate?: string;
    userId?: string;
  }): Promise<string> {
    try {
      const logs = await this.adminGetAuditLogs(filters);

      // Create CSV content
      const headers = ['Timestamp', 'Action', 'Action Type', 'Details', 'Target User', 'Admin Name', 'Admin Email', 'Severity'];
      const rows = logs.map(log => [
        new Date(log.timestamp).toLocaleString(),
        log.action,
        log.actionType || 'N/A',
        log.details,
        log.userId || 'N/A',
        log.adminUserName || 'N/A',
        log.adminUserEmail || 'N/A',
        log.severity,
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
      ].join('\n');

      return csvContent;
    } catch (error) {
      console.error('Error exporting audit logs:', error);
      throw error;
    }
  },
};
